import { Transformer } from 'markmap-lib'

// 颜色区分报告主题，不表示风险等级；风险仍以报告中的原文为准。
const THEMES = {
    数据支撑: ['#087d68', '#e9f8f1'],
    今日判断: ['#2459e8', '#edf3ff'],
    采购与供货: ['#ba6947', '#fbf0e9'],
    采购建议: ['#ba6947', '#fbf0e9'],
    供货与调拨: ['#ba6947', '#fbf0e9'],
    积压与优化: ['#a47b32', '#f8f2e6'],
    观察与核查: ['#428279', '#eaf4f1'],
}
const CONTEXT_TOPICS = new Set(['数据支撑', '昨日变动', '判断边界', '报告口径'])
const FOLDED_TOPICS = new Set(['今日判断', '判断边界', '报告口径'])
const REPAIR_THEMES = [
    ['#2459e8', '#edf3ff'],
    ['#087d68', '#e9f8f1'],
    ['#b94e1c', '#fff2e8'],
    ['#4954b8', '#f0f0ff'],
]

function wrapText(text, maxWidth, context) {
    const lines = []
    let line = ''
    // 中文逐字换行，数字与英文优先保持完整，超长编码再逐字拆分。
    const words = text.match(/[A-Za-z0-9][A-Za-z0-9.,_:/%+-]*|\s+|[^\s]/gu) || ['']
    words.forEach((word) => {
        const pieces = context.measureText(word).width > maxWidth ? Array.from(word) : [word]
        pieces.forEach((piece) => {
            if (line && context.measureText(line + piece).width > maxWidth) {
                lines.push(line.trim())
                line = ''
            }
            line += piece
        })
    })
    if (line.trim()) lines.push(line.trim())
    return lines.length ? lines : ['']
}

// Markmap 把表头、表体和行包装为空节点；按列名关联单元格，避免导图出现无标题卡片。
export function tableNodes(source) {
    let headers = []
    const children = []
    ;(source.children || []).forEach((child, index, siblings) => {
        if (child.type === 'thead') {
            headers = (child.children || []).map((cell) => cell.content)
            if (!siblings[index + 1] || !(siblings[index + 1].children || []).length) {
                children.push({ ...child, content: headers.join(' · '), children: [] })
            }
        } else if (child.type === 'tbody') {
            ;(child.children || []).forEach((row, rowIndex) => {
                const cells = (row.children || []).map((cell, column) => ({
                    ...cell,
                    content: `${headers[column] ? `${headers[column]}：` : ''}${cell.content}`,
                }))
                const [first, ...rest] = cells
                children.push({ ...row, content: first && first.content ? first.content : `第 ${rowIndex + 1} 行`, children: rest })
            })
        } else {
            children.push(tableNodes(child))
        }
    })
    return { ...source, children }
}

export function parseMindmap(markdown, reportType = 'inventory') {
    // 只使用 Markmap 的 Markdown 层级解析，不加载插件、外部资源或原始 HTML。
    const transformer = new Transformer([])
    transformer.md.set({ html: false, linkify: false })
    transformer.md.renderer.rules.image = () => ''
    const { root } = transformer.transform(markdown)
    if (!root) throw new Error('报告缺少导图内容')
    const context = document.createElement('canvas').getContext('2d')
    const font = window.getComputedStyle(document.body).fontFamily
    const parser = new DOMParser()

    const visit = (source, id, depth, theme, side) => {
        const text = parser.parseFromString(source.content, 'text/html').body.textContent.trim()
        const fontSize = depth === 0 ? 17 : 14
        const weight = depth < 2 ? 600 : 400
        context.font = `${weight} ${fontSize}px ${font}`
        const maxWidth = depth === 0 ? 156 : depth === 1 ? 126 : 232
        // 根节点把机构名与报告类型分行，避免把“库存分析”截成两半。
        const displayText = depth === 0 ? text.replace(/库存分析$/, '\n库存分析') : text
        const lines = displayText.split('\n').flatMap((part) => wrapText(part, maxWidth, context))
        const width = depth === 0 ? 192 : Math.max(depth === 1 ? 112 : 140, ...lines.map((line) => context.measureText(line).width + 32))
        const lineHeight = depth === 0 ? 26 : 23
        const height = lines.length * lineHeight + (depth === 0 ? 32 : 20)
        return {
            id,
            text,
            depth,
            theme,
            side,
            lines,
            width,
            height,
            lineHeight,
            children: (source.children || []).map((child, index) => {
                const label = parser.parseFromString(child.content, 'text/html').body.textContent.trim()
                return visit(
                    child,
                    `${id}.${index}`,
                    depth + 1,
                    depth === 0 ? (reportType === 'repair' ? REPAIR_THEMES[index % REPAIR_THEMES.length] : THEMES[label] || ['#7c8798', '#f0f2f5']) : theme,
                    // 维修报告章节不套用库存分类，左右交错排布以充分利用弹层画布。
                    depth === 0 ? (reportType === 'repair' ? (index % 2 ? 1 : -1) : CONTEXT_TOPICS.has(label) ? -1 : 1) : side
                )
            }),
        }
    }
    return visit(tableNodes(root), 'root', 0, ['#2459e8', '#2459e8'], 0)
}

export function overviewFolds(root) {
    const folded = new Set()
    const visit = (node) => {
        if (node.children.length && (node.depth >= 2 || FOLDED_TOPICS.has(node.text))) folded.add(node.id)
        node.children.forEach(visit)
    }
    visit(root)
    return folded
}

export function layoutMindmap(root, folded) {
    const nodes = []
    const links = []
    const measure = (node) => {
        const children = folded.has(node.id) ? [] : node.children.map(measure)
        const childrenHeight = children.reduce((sum, child) => sum + child.span, 0) + Math.max(0, children.length - 1) * 12
        return { ...node, children, span: Math.max(node.height, childrenHeight), childCount: node.children.length }
    }
    const center = { ...root, x: -root.width / 2, y: 0, childCount: 0 }
    nodes.push(center)
    const place = (node, parent, y) => {
        const gap = node.depth === 1 ? 64 : 36
        const x = node.side === 1 ? parent.x + parent.width + gap : parent.x - gap - node.width
        const positioned = { ...node, x, y }
        nodes.push(positioned)
        links.push({ from: parent, to: positioned })
        const total = node.children.reduce((sum, child) => sum + child.span, 0) + Math.max(0, node.children.length - 1) * 12
        let top = y - total / 2
        node.children.forEach((child) => {
            place(child, positioned, top + child.span / 2)
            top += child.span + 12
        })
    }
    // 左侧保留数据与口径，右侧聚焦判断与行动；两侧各自排布，避免长分支挤压整张图。
    ;[-1, 1].forEach((side) => {
        const branches = root.children.filter((child) => child.side === side).map(measure)
        const height = branches.reduce((sum, node) => sum + node.span, 0) + Math.max(0, branches.length - 1) * 28
        let top = -height / 2
        branches.forEach((node) => {
            place(node, center, top + node.span / 2)
            top += node.span + 28
        })
    })
    const bounds = {
        left: Math.min(...nodes.map((node) => node.x)) - 16,
        right: Math.max(...nodes.map((node) => node.x + node.width)) + 16,
        top: Math.min(...nodes.map((node) => node.y - node.height / 2)) - 16,
        bottom: Math.max(...nodes.map((node) => node.y + node.height / 2)) + 16,
    }
    return { nodes, links, bounds }
}

export function branchPath({ from, to }) {
    const start = to.side === 1 ? from.x + from.width : from.x
    const end = to.side === 1 ? to.x : to.x + to.width
    const middle = (start + end) / 2
    return `M ${start},${from.y} C ${middle},${from.y} ${middle},${to.y} ${end},${to.y}`
}
